import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Show } from "@clerk/react";
import SignUpPage from "@/pages/SignUpPage";
import OwnershipChart from "@/pages/OwnershipChart";
import SignInPage, { ResetPassword } from "@/pages/SignInPage";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import AuthShell from "@/pages/AuthShell";
import Pending from "@/pages/Pending";
import Dashboard from "@/pages/Dashboard";
import Uploads from "@/pages/Uploads";
import Entities from "@/pages/Entities";
import Ownership from "@/pages/Ownership";
import EntityReports from "@/pages/EntityReports";
import NetWorth from "@/pages/NetWorth";
import UsersPage from "@/pages/UsersPage";
import Comparison from "@/pages/Comparison";
import Onboarding from "@/pages/Onboarding";
import ClientPortal from "@/pages/ClientPortal";
import Billing from "@/pages/Billing";
import { PaymentCancel, PaymentSuccess } from "@/pages/PaymentResult";
import ConsoleLayout from "@/console/ConsoleLayout";
import ConsoleFirms from "@/console/ConsoleFirms";
import ConsoleFirm from "@/console/ConsoleFirm";
import ConsolePricing from "@/console/ConsolePricing";
import { ConsoleAudit, ConsoleLogins, ConsolePayments } from "@/console/ConsoleLists";

function ConsoleRoutes() {
  return (
    <Routes>
      <Route path="console" element={<ConsoleLayout />}>
        <Route index element={<ConsoleFirms />} />
        <Route path="firms/:id" element={<ConsoleFirm />} />
        <Route path="logins" element={<ConsoleLogins />} />
        <Route path="pricing" element={<ConsolePricing />} />
        <Route path="payments" element={<ConsolePayments />} />
        <Route path="audit" element={<ConsoleAudit />} />
      </Route>
      <Route path="*" element={<Navigate to="/console" replace />} />
    </Routes>
  );
}

function Gate() {
  const { me, meError } = useApp();
  if (meError) return <Pending error />;
  if (!me) return <div className="p-10 small-label" data-testid="app-loading">Loading…</div>;
  if (me.role === "owner") return <ConsoleRoutes />;
  if (me.role === "client" && me.tenant_id) return <ClientPortal />;
  if (!me.tenant_id) return <Onboarding />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="uploads" element={<Uploads />} />
        <Route path="entities" element={<Entities />} />
        <Route path="ownership" element={<Ownership />} />
        <Route path="ownership-chart" element={<OwnershipChart />} />
        <Route path="reports/entities" element={<EntityReports />} />
        <Route path="reports/entities/:id" element={<EntityReports />} />
        <Route path="reports/networth" element={<NetWorth />} />
        <Route path="reports/networth/:id" element={<NetWorth />} />
        <Route path="comparison" element={<Comparison />} />
        <Route path="billing" element={<Billing />} />
        <Route path="payment/success" element={<PaymentSuccess />} />
        <Route path="payment/cancel" element={<PaymentCancel />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function Protected() {
  return (
    <>
      <Show when="signed-in">
        <AppProvider>
          <Gate />
        </AppProvider>
      </Show>
      <Show when="signed-out">
        <Navigate to="/sign-in" replace />
      </Show>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/sign-in/reset/*" element={<AuthShell><ResetPassword /></AuthShell>} />
          <Route path="/sign-in/*" element={<AuthShell><SignInPage /></AuthShell>} />
          <Route path="/sign-up/*" element={<AuthShell><SignUpPage /></AuthShell>} />
          <Route path="/*" element={<Protected />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
